'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackEvent } from '@/lib/analytics';
import { submitPipelineALead, submitPipelineBLead } from '@/lib/leadService';

interface PipelineLeadFormsProps {
  currentLanguage?: string;
}

interface PipelineAForm {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  sector: string;
  emissionsBaseline: string;
  complianceDeadline: string;
  pipeline: 'A';
}

interface PipelineBForm {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  vendorType: string;
  targetMarket: string;
  brsrStatus: string;
  pipeline: 'B';
}

type ActivePipeline = 'A' | 'B';

export default function PipelineLeadForms({ currentLanguage = 'en' }: PipelineLeadFormsProps) {
  const [activePipeline, setActivePipeline] = useState<ActivePipeline>('A');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<ActivePipeline | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formA, setFormA] = useState<PipelineAForm>({
    fullName: '', email: '', phone: '', company: '',
    sector: '', emissionsBaseline: '', complianceDeadline: '', pipeline: 'A',
  });

  const [formB, setFormB] = useState<PipelineBForm>({
    fullName: '', email: '', phone: '', company: '',
    vendorType: '', targetMarket: '', brsrStatus: '', pipeline: 'B',
  });

  const [errorsA, setErrorsA] = useState<Partial<Record<keyof PipelineAForm, string>>>({});
  const [errorsB, setErrorsB] = useState<Partial<Record<keyof PipelineBForm, string>>>({});

  const sectorOptions = [
    'Data Center / IT', 'Manufacturing', 'PSU / Government', 'Real Estate / IGBC',
    'Ports & Logistics', 'Railways', 'Hospitality', 'Agriculture / Plantation',
    'CA / Consulting Firm', 'BRSR AI Automation', 'Other'
  ];

  const vendorTypeOptions = [
    'IT Services Vendor', 'Manufacturing Supplier', 'Logistics Provider',
    'Professional Services', 'CA / Accounting Firm', 'Construction / Infrastructure',
    'Agriculture / Food', 'Ancestral Land / Farm Owner', 'Other'
  ];

  const targetMarketOptions = [
    'UK / Europe (CSRD)', 'Australia (CPA)', 'India (BRSR)', 'UAE / Middle East', 'USA', 'Multiple Markets'
  ];

  const brsrStatusOptions = [
    'Not Started', 'Aware but not compliant', 'Partially compliant', 'Fully compliant'
  ];

  const deadlineOptions = [
    'Immediate (< 3 months)', 'Q3 2025', 'Q4 2025', 'FY 2025-26', 'FY 2026-27', 'Exploring options'
  ];

  const validateA = () => {
    const e: Partial<Record<keyof PipelineAForm, string>> = {};
    if (!formA.fullName.trim()) e.fullName = 'Required';
    if (!formA.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formA.email)) e.email = 'Valid email required';
    if (!formA.company.trim()) e.company = 'Required';
    if (!formA.sector) e.sector = 'Required';
    setErrorsA(e);
    return Object.keys(e).length === 0;
  };

  const validateB = () => {
    const e: Partial<Record<keyof PipelineBForm, string>> = {};
    if (!formB.fullName.trim()) e.fullName = 'Required';
    if (!formB.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formB.email)) e.email = 'Valid email required';
    if (!formB.company.trim()) e.company = 'Required';
    if (!formB.vendorType) e.vendorType = 'Required';
    if (!formB.targetMarket) e.targetMarket = 'Required';
    setErrorsB(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmitA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateA()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Save to Supabase
      const result = await submitPipelineALead({
        fullName: formA.fullName,
        email: formA.email,
        phone: formA.phone,
        company: formA.company,
        sector: formA.sector,
        emissionsBaseline: formA.emissionsBaseline,
        complianceDeadline: formA.complianceDeadline,
      });

      if (!result.success) {
        throw new Error(result.error || 'Submission failed');
      }

      // Also send to API for email routing
      await fetch('/api/brsr-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formA, partnerType: 'corporate', complianceFramework: 'brsr', pipelineTag: 'Pipeline-A-NetZeroAudit' }),
      });

      // GA4 conversion event
      trackEvent('pipeline_a_form_submit', {
        event_category: 'Lead Generation',
        event_label: 'Pipeline A - Net Zero Audit',
        sector: formA.sector,
        company: formA.company,
        value: 1,
      });
      trackEvent('generate_lead', {
        currency: 'INR',
        value: 50000,
        lead_source: 'Pipeline_A',
        sector: formA.sector,
      });

      setSubmitSuccess('A');
    } catch (err: any) {
      setSubmitError('Submission failed. Please email nupurbanker@gmail.com directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitB = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateB()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Save to Supabase
      const result = await submitPipelineBLead({
        fullName: formB.fullName,
        email: formB.email,
        phone: formB.phone,
        company: formB.company,
        vendorType: formB.vendorType,
        targetMarket: formB.targetMarket,
        brsrStatus: formB.brsrStatus,
      });

      if (!result.success) {
        throw new Error(result.error || 'Submission failed');
      }

      // Also send to API for email routing
      await fetch('/api/brsr-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formB, partnerType: 'sme', complianceFramework: 'brsr', pipelineTag: 'Pipeline-B-BRSRLiteChecklist' }),
      });

      // GA4 conversion event
      trackEvent('pipeline_b_form_submit', {
        event_category: 'Lead Generation',
        event_label: 'Pipeline B - BRSR Lite Checklist',
        vendor_type: formB.vendorType,
        target_market: formB.targetMarket,
        value: 1,
      });
      trackEvent('generate_lead', {
        currency: 'INR',
        value: 20000,
        lead_source: 'Pipeline_B',
        vendor_type: formB.vendorType,
      });

      setSubmitSuccess('B');
    } catch (err: any) {
      setSubmitError('Submission failed. Please email nupurbanker@gmail.com directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForms = () => {
    setSubmitSuccess(null);
    setSubmitError(null);
    setFormA({ fullName: '', email: '', phone: '', company: '', sector: '', emissionsBaseline: '', complianceDeadline: '', pipeline: 'A' });
    setFormB({ fullName: '', email: '', phone: '', company: '', vendorType: '', targetMarket: '', brsrStatus: '', pipeline: 'B' });
  };

  if (submitSuccess) {
    return (
      <section id="lead-capture" className="py-16 sm:py-20 bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl text-center">
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 sm:p-12 border border-emerald-400/30">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={40} className="text-emerald-400" />
            </div>
            <div className="text-4xl mb-4">{submitSuccess === 'A' ? '🎯' : '✅'}</div>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {submitSuccess === 'A' ? 'Your Free Net Zero Audit is Confirmed!' : 'Your Free BRSR Lite Checklist is on its way!'}
            </h3>
            <p className="text-emerald-200 text-base sm:text-lg mb-6">
              {submitSuccess === 'A' ? "Dr. Nupur's team will contact you within 24 hours to schedule your complimentary Net Zero Readiness Assessment." :"We'll send your BRSR Lite Checklist within 24 hours. Our team will also reach out to discuss your empanelment readiness."}
            </p>
            <div className="bg-emerald-500/10 rounded-xl p-4 mb-6 border border-emerald-400/20">
              <p className="text-slate-300 text-sm">
                📧 Confirmation sent to your email · Contact: <strong className="text-emerald-300">nupurbanker@gmail.com</strong>
              </p>
              <p className="text-slate-400 text-xs mt-1">🌐 <a href="https://www.gencarbon.in" className="text-emerald-400 hover:underline">www.gencarbon.in</a></p>
            </div>
            <button
              onClick={resetForms}
              className="px-8 py-3 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-400 transition-all duration-300"
            >
              Submit Another Inquiry
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="lead-capture" className="py-16 sm:py-20 bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {/* Section Header — GenZ friendly */}
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 bg-emerald-500/20 rounded-full mb-5 border border-emerald-400/30">
            <Icon name="BoltIcon" size={16} className="text-emerald-400" />
            <span className="text-xs sm:text-sm font-semibold text-emerald-300">Free Consultation — No Commitment Required</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
            Start Your ESG Journey <span className="text-emerald-400">Today</span>
          </h2>
          <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto px-2">
            Whether you're referring your family's farm land for carbon credits or scaling your business for global markets — we've got you covered.
          </p>

          {/* Social proof bar */}
          <div className="flex flex-wrap justify-center gap-3 mt-5">
            {['🌾 Ancestral Land Owners', '💻 IT & Vendor Firms', '📊 CAs & BRSR Consultants', '🏢 Enterprise & PSUs'].map((tag, i) => (
              <span key={i} className="text-xs font-medium px-3 py-1.5 bg-white/10 text-slate-300 rounded-full border border-white/10">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Pipeline Toggle — Mobile-first */}
        <div className="flex justify-center mb-8 sm:mb-10">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-1.5 sm:p-2 flex gap-1.5 sm:gap-2 border border-white/20 w-full max-w-sm sm:max-w-none sm:w-auto">
            <button
              onClick={() => setActivePipeline('A')}
              className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 ${
                activePipeline === 'A' ? 'bg-emerald-500 text-white shadow-lg' : 'text-slate-300 hover:text-white hover:bg-white/10'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5 sm:gap-2">
                <Icon name="BuildingOffice2Icon" size={16} />
                <span className="hidden sm:inline">Enterprise &amp; Government</span>
                <span className="sm:hidden">Enterprise</span>
              </span>
            </button>
            <button
              onClick={() => setActivePipeline('B')}
              className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 ${
                activePipeline === 'B' ? 'bg-amber-500 text-white shadow-lg' : 'text-slate-300 hover:text-white hover:bg-white/10'
              }`}
            >
              <span className="flex items-center justify-center gap-1.5 sm:gap-2">
                <Icon name="LinkIcon" size={16} />
                <span className="hidden sm:inline">Vendors &amp; Farms</span>
                <span className="sm:hidden">Vendors</span>
              </span>
            </button>
          </div>
        </div>

        {/* Error message */}
        {submitError && (
          <div className="max-w-2xl mx-auto mb-6 p-4 bg-red-500/10 border border-red-400/30 rounded-xl text-red-300 text-sm text-center">
            {submitError}
          </div>
        )}

        {/* Forms — stacked on mobile, side-by-side on desktop */}
        <div className="max-w-5xl mx-auto">

          {/* Pipeline A Form */}
          <div className={`transition-all duration-500 ${activePipeline === 'A' ? 'block' : 'hidden lg:block lg:opacity-50 lg:pointer-events-none'}`}>
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border-2 border-emerald-500/40 hover:border-emerald-400/60 transition-all duration-300">
              {/* Header */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="BuildingOffice2Icon" size={22} className="text-emerald-400" />
                </div>
                <div>
                  <span className="text-xs font-bold text-emerald-400 uppercase tracking-widest">Pipeline A — Enterprise</span>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Free Net Zero Audit</h3>
                  <p className="text-slate-300 text-sm mt-1">For enterprises, data centers, PSUs, CAs &amp; government bodies seeking carbon credit generation and Net Zero compliance.</p>
                </div>
              </div>

              {/* Value Props — 2-col grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-6">
                {[
                  { icon: 'ClockIcon', text: '6–18 month credit timelines' },
                  { icon: 'ChartBarIcon', text: '2.5–3× higher carbon yields' },
                  { icon: 'CpuChipIcon', text: 'Quantum-classical MRV' },
                  { icon: 'ShieldCheckIcon', text: 'IGBC/LEED certified' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 bg-emerald-500/10 rounded-lg px-2.5 sm:px-3 py-2">
                    <Icon name={item.icon as any} size={14} className="text-emerald-400 flex-shrink-0" />
                    <span className="text-xs text-emerald-200 font-medium leading-tight">{item.text}</span>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmitA} className="space-y-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={formA.fullName}
                      onChange={e => setFormA(p => ({ ...p, fullName: e.target.value }))}
                      placeholder="Dr. / Mr. / Ms."
                      className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all ${errorsA.fullName ? 'border-red-400' : 'border-white/20'}`}
                    />
                    {errorsA.fullName && <p className="text-red-400 text-xs mt-1">{errorsA.fullName}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Work Email *</label>
                    <input
                      type="email"
                      value={formA.email}
                      onChange={e => setFormA(p => ({ ...p, email: e.target.value }))}
                      placeholder="you@company.com"
                      className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all ${errorsA.email ? 'border-red-400' : 'border-white/20'}`}
                    />
                    {errorsA.email && <p className="text-red-400 text-xs mt-1">{errorsA.email}</p>}
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Phone / WhatsApp</label>
                    <input
                      type="tel"
                      value={formA.phone}
                      onChange={e => setFormA(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Company / Organisation *</label>
                    <input
                      type="text"
                      value={formA.company}
                      onChange={e => setFormA(p => ({ ...p, company: e.target.value }))}
                      placeholder="Company name"
                      className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all ${errorsA.company ? 'border-red-400' : 'border-white/20'}`}
                    />
                    {errorsA.company && <p className="text-red-400 text-xs mt-1">{errorsA.company}</p>}
                  </div>
                </div>

                {/* Sector */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Industry Sector *</label>
                  <select
                    value={formA.sector}
                    onChange={e => setFormA(p => ({ ...p, sector: e.target.value }))}
                    className={`w-full px-4 py-3 bg-slate-800 border rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all ${errorsA.sector ? 'border-red-400' : 'border-white/20'}`}
                  >
                    <option value="">Select sector...</option>
                    {sectorOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errorsA.sector && <p className="text-red-400 text-xs mt-1">{errorsA.sector}</p>}
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Emissions Baseline</label>
                    <input
                      type="text"
                      value={formA.emissionsBaseline}
                      onChange={e => setFormA(p => ({ ...p, emissionsBaseline: e.target.value }))}
                      placeholder="e.g. 5,000 tCO₂e/yr"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Compliance Deadline</label>
                    <select
                      value={formA.complianceDeadline}
                      onChange={e => setFormA(p => ({ ...p, complianceDeadline: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-800 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all"
                    >
                      <option value="">Select timeline...</option>
                      {deadlineOptions.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-white font-bold rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-emerald-500/30 disabled:opacity-60 flex items-center justify-center gap-2 text-base active:scale-95"
                >
                  {isSubmitting ? (
                    <><Icon name="ArrowPathIcon" size={20} className="animate-spin" /> Submitting...</>
                  ) : (
                    <><Icon name="DocumentMagnifyingGlassIcon" size={20} /> Get My Free Net Zero Audit</>
                  )}
                </button>
                <p className="text-center text-xs text-slate-400">
                  🔒 Secure · No spam · Response within 24 hours · <a href="https://www.gencarbon.in" className="text-emerald-400">www.gencarbon.in</a>
                </p>
              </form>
            </div>
          </div>

          {/* Pipeline B Form */}
          <div className={`transition-all duration-500 mt-6 lg:mt-0 ${activePipeline === 'B' ? 'block' : 'hidden lg:block lg:opacity-50 lg:pointer-events-none'}`}>
            <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border-2 border-amber-500/40 hover:border-amber-400/60 transition-all duration-300">
              {/* Header */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="LinkIcon" size={22} className="text-amber-400" />
                </div>
                <div>
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">Pipeline B — Vendors &amp; Farms</span>
                  <h3 className="text-xl sm:text-2xl font-bold text-white">Free BRSR Lite Checklist</h3>
                  <p className="text-slate-300 text-sm mt-1">For value chain vendors, IT firms, CAs, and ancestral land / farm owners seeking empanelment or carbon credits.</p>
                </div>
              </div>

              {/* Value Props */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-6">
                {[
                  { icon: 'CheckBadgeIcon', text: 'Empanelment-ready fast' },
                  { icon: 'GlobeAltIcon', text: 'UK / EU / AU markets' },
                  { icon: 'DocumentTextIcon', text: 'BRSR Lite compliant' },
                  { icon: 'CurrencyRupeeIcon', text: 'Win more contracts' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 bg-amber-500/10 rounded-lg px-2.5 sm:px-3 py-2">
                    <Icon name={item.icon as any} size={14} className="text-amber-400 flex-shrink-0" />
                    <span className="text-xs text-amber-200 font-medium leading-tight">{item.text}</span>
                  </div>
                ))}
              </div>

              {/* GenZ-friendly note for farm/land owners */}
              <div className="bg-amber-500/10 border border-amber-400/20 rounded-xl p-3 mb-5">
                <p className="text-xs text-amber-200">
                  🌾 <strong>Referring your family's farm or ancestral land?</strong> You can earn carbon credits for your parents while helping the planet. Select "Ancestral Land / Farm Owner" below.
                </p>
              </div>

              <form onSubmit={handleSubmitB} className="space-y-4">
                {/* Row 1 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Full Name *</label>
                    <input
                      type="text"
                      value={formB.fullName}
                      onChange={e => setFormB(p => ({ ...p, fullName: e.target.value }))}
                      placeholder="Your name"
                      className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all ${errorsB.fullName ? 'border-red-400' : 'border-white/20'}`}
                    />
                    {errorsB.fullName && <p className="text-red-400 text-xs mt-1">{errorsB.fullName}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Email *</label>
                    <input
                      type="email"
                      value={formB.email}
                      onChange={e => setFormB(p => ({ ...p, email: e.target.value }))}
                      placeholder="you@company.com"
                      className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all ${errorsB.email ? 'border-red-400' : 'border-white/20'}`}
                    />
                    {errorsB.email && <p className="text-red-400 text-xs mt-1">{errorsB.email}</p>}
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Phone / WhatsApp</label>
                    <input
                      type="tel"
                      value={formB.phone}
                      onChange={e => setFormB(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Company / Business Name *</label>
                    <input
                      type="text"
                      value={formB.company}
                      onChange={e => setFormB(p => ({ ...p, company: e.target.value }))}
                      placeholder="Your business or farm name"
                      className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all ${errorsB.company ? 'border-red-400' : 'border-white/20'}`}
                    />
                    {errorsB.company && <p className="text-red-400 text-xs mt-1">{errorsB.company}</p>}
                  </div>
                </div>

                {/* Vendor Type */}
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Business / Vendor Type *</label>
                  <select
                    value={formB.vendorType}
                    onChange={e => setFormB(p => ({ ...p, vendorType: e.target.value }))}
                    className={`w-full px-4 py-3 bg-slate-800 border rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all ${errorsB.vendorType ? 'border-red-400' : 'border-white/20'}`}
                  >
                    <option value="">Select type...</option>
                    {vendorTypeOptions.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                  {errorsB.vendorType && <p className="text-red-400 text-xs mt-1">{errorsB.vendorType}</p>}
                </div>

                {/* Row 3 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Target Market *</label>
                    <select
                      value={formB.targetMarket}
                      onChange={e => setFormB(p => ({ ...p, targetMarket: e.target.value }))}
                      className={`w-full px-4 py-3 bg-slate-800 border rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all ${errorsB.targetMarket ? 'border-red-400' : 'border-white/20'}`}
                    >
                      <option value="">Select market...</option>
                      {targetMarketOptions.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    {errorsB.targetMarket && <p className="text-red-400 text-xs mt-1">{errorsB.targetMarket}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Current BRSR Status</label>
                    <select
                      value={formB.brsrStatus}
                      onChange={e => setFormB(p => ({ ...p, brsrStatus: e.target.value }))}
                      className="w-full px-4 py-3 bg-slate-800 border border-white/20 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 transition-all"
                    >
                      <option value="">Select status...</option>
                      {brsrStatusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-amber-500 hover:bg-amber-400 text-white font-bold rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/30 disabled:opacity-60 flex items-center justify-center gap-2 text-base active:scale-95"
                >
                  {isSubmitting ? (
                    <><Icon name="ArrowPathIcon" size={20} className="animate-spin" /> Submitting...</>
                  ) : (
                    <><Icon name="DocumentArrowDownIcon" size={20} /> Get My Free BRSR Lite Checklist</>
                  )}
                </button>
                <p className="text-center text-xs text-slate-400">
                  🔒 Secure · No spam · Checklist within 24 hours · <a href="https://www.gencarbon.in" className="text-amber-400">www.gencarbon.in</a>
                </p>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <p className="text-center text-slate-400 text-sm mt-8">
          Both pipelines route to <strong className="text-emerald-300">nupurbanker@gmail.com</strong> · Response within 24 business hours · <a href="https://www.gencarbon.in" className="text-emerald-400 hover:underline">www.gencarbon.in</a>
        </p>
      </div>
    </section>
  );
}
